// ABOUTME: Modal for regenerating MFA recovery codes.
// ABOUTME: First verifies TOTP code, then displays new codes with download option.
import { Box, useTheme } from "@mui/material";
import OtpInput from "../../theme/inputs/OtpInput";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import SHModal from "../../theme/Modal/SHModal";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import Shbutton from "../../theme/buttons/Shbutton";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { tokens } from "../../../theme";
import { useRegenerateCodesMutation } from "../../../newapi/user/mfaSlice";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

const MConfirmRegenerateCodes = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const [code, setCode] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [newCodes, setNewCodes] = useState(null);
    const [regenerateCodes, { isLoading }] = useRegenerateCodesMutation();

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const handleConfirm = async (codeOverride) => {
        setErrorMessage("");
        try {
            const result = await regenerateCodes({ code: codeOverride || code }).unwrap();
            if (result.success) {
                setNewCodes(result.data.recoveryCodes);
                setCode("");
            } else {
                setErrorMessage(result.error || t("invalid_code_try_again"));
            }
        } catch (err) {
            setErrorMessage(err?.data?.error || t("invalid_code_try_again"));
        }
    };

    const handleDownload = () => {
        if (!newCodes) return;
        const content = [
            "StockHub Recovery Codes",
            "========================",
            "Each code can only be used once.",
            "",
            ...newCodes,
            "",
            `Generated: ${new Date().toISOString()}`,
        ].join("\n");

        const blob = new Blob([content], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "stockhub-recovery-codes.txt";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && code.length === 6 && !newCodes) {
            handleConfirm();
        }
    };

    const handleClose = () => {
        setCode("");
        setErrorMessage("");
        setNewCodes(null);
        props.handleClose();
    };

    const showCodesView = !!newCodes;

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
            onKeyDown={handleKeyDown}
            sx={{
                body: {
                    width:500,
                }
            }}
        >
            <Box
                sx={{
                    textAlign: "center",
                    py: 4,
                    px: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems:'center',
                    maxWidth: 500
                }}
            >
                <Subheading>
                    {showCodesView ? t("save_recovery_codes") : t("regenerate_recovery_codes")}
                </Subheading>
                <Text center light>
                    {showCodesView ? t("recovery_codes_warning") : t("confirm_regenerate_codes_subtitle")}
                </Text>
            </Box>

            <Box sx={{
                background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                p: 3,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                textAlign: "center",
            }}>
                {!showCodesView && (
                    <>
                        <OtpInput
                            value={code}
                            onChange={(val) => { setCode(val); setErrorMessage(""); }}
                            onComplete={(val) => handleConfirm(val)}
                            error={!!errorMessage}
                            autoFocus
                        />
                        {errorMessage && (
                            <Text sx={{ color: colors.red[400], mt: 1 }}>{errorMessage}</Text>
                        )}
                    </>
                )}

                {showCodesView && (
                    <Box>
                        <Box
                            sx={{
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.orange[100],
                                border: `1px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.orange[400]}`,
                                borderRadius: "8px",
                                p: 2,
                                mb: 3,
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                textAlign: "left",
                            }}
                        >
                            <WarningAmberOutlinedIcon sx={{ color: colors.orange[400] }} />
                            <Text sx={{ fontSize: "13px", color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.orange[400] }}>
                                {t("recovery_codes_warning")}
                            </Text>
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 1,
                                mb: 3,
                            }}
                        >
                            {newCodes.map((c, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        fontFamily: "monospace",
                                        fontSize: "14px",
                                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                        px: 2,
                                        borderRadius: 6,
                                        height:32,
                                        lineHeight:'32px',
                                        textAlign: "center",
                                        userSelect: "all",
                                    }}
                                >
                                    <Text>
                                        {c}
                                    </Text>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Shbutton
                                variant="outlined"
                                color={theme.palette.mode === "dark" ? "primaryGrey" : "grey"}
                                onClick={handleDownload}
                                startIcon={<DownloadOutlinedIcon />}
                                block
                            >
                                {t("download_as_txt")}
                            </Shbutton>
                        </Box>
                    </Box>
                )}
            </Box>

            <Box
                component="footer"
                sx={{
                    display: "flex",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    alignItems: "center",
                    paddingRight: 3,
                    paddingLeft: 3,
                    zIndex: 1058,
                    position: "relative",
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    justifyContent: showCodesView ? "flex-end" : "space-between",
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                        paddingLeft: 0,
                    }
                }}
            >
                {!showCodesView && (
                    <>
                        <Shbutton
                            isCancel={true}
                            className="always-visible"
                            onClick={handleClose}
                            color="primary"
                            variant="text"
                            startIcon={<ArrowRightAltIcon />}
                        >
                            {t("cancel")}
                        </Shbutton>
                        <ButtonGroup version={2}>
                            <Shbutton
                                className="group-btn"
                                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                onClick={() => handleConfirm()}
                                variant="contained"
                                disabled={code.length !== 6}
                                isLoading={isLoading}
                            >
                                {t("regenerate")}
                            </Shbutton>
                        </ButtonGroup>
                    </>
                )}
                {showCodesView && (
                    <ButtonGroup version={2}>
                        <Shbutton
                            className="group-btn"
                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                            onClick={handleClose}
                            variant="contained"
                        >
                            {t("done")}
                        </Shbutton>
                    </ButtonGroup>
                )}
            </Box>
        </SHModal>
    );
};

export default MConfirmRegenerateCodes;
